import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { attendeeAPI, feedbackAPI, brandingAPI, resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Input } from '../components/ui/input';
import { 
  CheckCircle,
  Loader2,
  Download,
  AlertCircle
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FeedbackForm() {
  const { eventId, attendeeId } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [formData, setFormData] = useState({
    objectives_met: { answer: '', other_text: '' },
    evidence_based: { answer: '', other_text: '' },
    commercial_bias: { answer: '', other_text: '' },
    clinical_influence: { answer: '', other_text: '' },
    suggestions: ''
  });

  const questions = [
    { 
      key: 'objectives_met', 
      label: 'Did the session meet the stated learning objectives?' 
    },
    { 
      key: 'evidence_based', 
      label: 'Was the content evidence-based and balanced?' 
    },
    { 
      key: 'commercial_bias', 
      label: 'Was there perceived commercial bias?' 
    },
    { 
      key: 'clinical_influence', 
      label: 'Will this influence your clinical practice?' 
    },
  ];

  useEffect(() => {
    checkIfCanDownload();
    brandingAPI.getPublic().then((r) => setBranding(r.data)).catch(() => {});
  }, [attendeeId]);

  const checkIfCanDownload = async () => {
    try {
      const response = await attendeeAPI.canDownload(attendeeId);
      if (response.data.feedback_submitted) {
        setSubmitted(true);
        setCanDownload(true);
      }
    } catch (error) {
      console.error('Error checking download status:', error);
    }
  };

  const handleAnswerChange = (questionKey, answer) => {
    setFormData({
      ...formData,
      [questionKey]: { ...formData[questionKey], answer }
    });
  };

  const handleOtherTextChange = (questionKey, text) => {
    setFormData({
      ...formData,
      [questionKey]: { ...formData[questionKey], other_text: text }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all questions are answered
    const unanswered = questions.filter(q => !formData[q.key].answer);
    if (unanswered.length > 0) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      await feedbackAPI.submit(eventId, {
        attendee_id: attendeeId,
        ...formData
      });
      setSubmitted(true);
      setCanDownload(true);
      toast.success('Feedback submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadCertificate = async () => {
    setDownloading(true);
    try {
      const response = await attendeeAPI.downloadCertificatePublic(attendeeId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'certificate.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  if (submitted && canDownload) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Toaster richColors position="top-center" />
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <img
              src={branding?.logo_url ? resolveAssetUrl(branding.logo_url) : '/iida-logo.png'}
              alt={branding?.organization_name || 'IIDA'}
              className="h-20 w-20 object-contain mx-auto mb-4"
            />
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Thank You for Your Feedback!</h2>
            <p className="text-muted-foreground mb-6">
              Your feedback helps us improve our educational activities.
            </p>
            <Button 
              onClick={handleDownloadCertificate}
              className="w-full gap-2"
              disabled={downloading}
              data-testid="download-certificate-btn"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download Your Certificate
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster richColors position="top-center" />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <img
            src={branding?.logo_url ? resolveAssetUrl(branding.logo_url) : '/iida-logo.png'}
            alt={branding?.organization_name || 'Illawarra Indian Doctors Association'}
            className="h-16 w-auto object-contain"
            data-testid="feedback-iida-logo"
          />
          <div>
            <h1 className="font-semibold text-foreground">{branding?.organization_name || 'Illawarra Indian Doctors Association'}</h1>
            <p className="text-xs text-muted-foreground">Event Feedback</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <Card className="border-border">
          <CardHeader>
            <CardTitle>Event Feedback</CardTitle>
            <CardDescription>
              Please complete this feedback form to download your certificate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 p-3 mb-6 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>Feedback is required to download your certificate of attendance.</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {questions.map((question) => (
                <div key={question.key} className="space-y-4">
                  <Label className="text-base font-medium">{question.label}</Label>
                  <RadioGroup
                    value={formData[question.key].answer}
                    onValueChange={(value) => handleAnswerChange(question.key, value)}
                    className="flex flex-col gap-3"
                  >
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <RadioGroupItem value="yes" id={`${question.key}-yes`} />
                      <span className="font-normal">Yes</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <RadioGroupItem value="no" id={`${question.key}-no`} />
                      <span className="font-normal">No</span>
                    </label>
                    <div className="flex items-start space-x-3">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <RadioGroupItem value="other" id={`${question.key}-other`} />
                        <span className="font-normal">Other</span>
                      </label>
                    </div>
                    {formData[question.key].answer === 'other' && (
                      <Input
                        placeholder="Please specify..."
                        value={formData[question.key].other_text}
                        onChange={(e) => handleOtherTextChange(question.key, e.target.value)}
                        className="ml-7"
                      />
                    )}
                  </RadioGroup>
                </div>
              ))}

              <div className="space-y-2">
                <Label htmlFor="suggestions" className="text-base font-medium">
                  Suggestions for Improvement
                </Label>
                <Textarea
                  id="suggestions"
                  placeholder="Any feedback to help us improve future events..."
                  value={formData.suggestions}
                  onChange={(e) => setFormData({ ...formData, suggestions: e.target.value })}
                  rows={4}
                  data-testid="feedback-suggestions"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12"
                disabled={submitting}
                data-testid="submit-feedback-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback & Get Certificate'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
